/**
 * Members state — the member list and role catalog, plus invite,
 * role-edit, and grant write intents. Presentation lives in the computed
 * view-models below; views read those and write intents through
 * membersAction$ — members.sync.ts (web-side) is the sole subscriber,
 * performing the Firestore writes and callable call, and reconciling
 * ok/error back via membersResult$, keyed by requestId so a view only
 * reacts to the result of the request it made.
 *
 * Grant assign/remove covers every member, whatever their scope: an
 * all-events member holds one grant (at the org id); an event-scoped
 * member can hold several, one per event (member.entity.ts: "a member
 * scoped to three events is still one person in one member list"). The
 * write intent below carries the member's own `scope` unchanged — the web
 * seam (members.api.ts) resolves it into the exact grant-doc id(s) to
 * write or delete, so one "change role" or "remove" click reaches every
 * grant the member actually holds.
 *
 * Pending invites render inline, grouped under their role beside active
 * members: `grouped` reads both variants of Member.MemberSchema straight
 * off membersState (Invite itself carries no FE read/list action — admins
 * read pending rows off the member mirror instead). The navigation
 * discriminant addresses a pending row the same way it addresses an active
 * one — by its own id (Detail's 'invite-detail' variant carries the
 * invite's token, mirroring 'member''s uid) — so `inviteDetailView` always
 * resolves the live row from membersState, whether it's an existing
 * member-list row or one just sent.
 */

import { formatDate } from "@mhmo91/rx";
import { computed, state } from "@mhmo91/schmancy/state";
import { translate } from "@momo/i18n";
import type { Grant, Member, Permission, Role } from "@momo/types";
import { uiLanguage } from "@momo/views";
import { Subject } from "rxjs";
import { activeActor } from "../auth/actor.state.ts";

// "Short month, numeric day/year" — e.g. "Jan 15, 2026". Shared by every
// invite-detail date label (invited-on, expires) for one consistent format.
const INVITE_DATE_FORMAT: Intl.DateTimeFormatOptions = {
	month: "short",
	day: "numeric",
	year: "numeric",
};

// Role's own schema excludes its doc id (no rule reads it as a field); the
// FE read seam attaches it from the snapshot doc, so the in-memory shape
// carries what the schema deliberately doesn't.
export type RoleWithId = Role.Role & { id: string };

/** A picker option for GrantScopeSchema's 'events' branch — id + display name only. */
export type EventOption = { readonly id: string; readonly name: string };

// ── Detail navigation discriminant ───────────────────────────────────────────

export type Detail =
	| { kind: "none" }
	| { kind: "member"; uid: string }
	| { kind: "invite" }
	| { kind: "invite-detail"; token: string }
	| { kind: "role"; id?: string };

// ── Invite form draft (UI-only fields; server fields added on send) ───────────

export type InviteDraft = {
	email: string;
	displayName: string;
	roleId: string;
	scope: Grant.GrantScope;
};

// ── Role edit draft (isolated from rolesState until committed) ────────────────

export type RoleDraft = {
	/**
	 * Which role this draft drafts — a role id when editing an existing role,
	 * `undefined` for a brand-new one. The draft outlives the surface that
	 * opened it (dismissing the sheet resets `detailState`, never the draft),
	 * so without this the next role opened renders whatever the last abandoned
	 * edit left behind: blank after an abandoned "New role", the previous
	 * role's name and permissions after an abandoned edit.
	 */
	roleId: string | undefined;
	name: string;
	perms: Set<Permission>;
	editing: boolean;
};

// ── Live state singletons ─────────────────────────────────────────────────────

/** Live member list from workspaces/{orgId}/members. */
export const membersState = state<readonly Member.Member[]>(
	"members/members",
).memory([]);

/** Live role catalog from workspaces/{orgId}/roles. */
export const rolesState = state<readonly RoleWithId[]>("members/roles").memory(
	[],
);

/**
 * Members data status — distinguishes "waiting for first snapshot" from
 * "snapshot arrived and is empty" so the view can show a loading affordance
 * instead of a premature empty state. "denied" is a Firestore permission
 * rejection (the actor lacks the read grant); "error" is everything else —
 * network, offline, a transient listener fault — so the view can tell the
 * two apart instead of blaming the connection for an access problem.
 */
export const membersStatus = state<"loading" | "ready" | "error" | "denied">(
	"members/status",
).memory("loading");

/** Event picker options for GrantScopeSchema's 'events' branch — id + name only. */
export const eventOptions = state<readonly EventOption[]>(
	"members/eventOptions",
).memory([]);

/**
 * Which detail panel is open. Written with `replace`, never `set`: `set`
 * shallow-merges, and merging one variant of a discriminated union onto
 * another leaves the previous variant's fields standing — closing a role
 * yields `{ kind: "none", id }`, a shape `Detail` does not have, and the next
 * `{ kind: "role" }` inherits that id, pointing the create form's Save at the
 * role last viewed.
 */
export const detailState = state<Detail>("members/detail").memory({
	kind: "none",
});

/** Invite-form draft — reset on open, discarded on cancel/send. */
export const inviteDraft = state<InviteDraft>("members/inviteDraft").memory({
	email: "",
	displayName: "",
	roleId: "",
	scope: { kind: "all-events", orgId: "" },
});

/** Isolated role-edit draft — rolesState mutated only on commit. */
export const roleDraft = state<RoleDraft>("members/roleDraft").memory({
	roleId: undefined,
	name: "",
	perms: new Set(),
	editing: false,
});

// ── Summary computeds ─────────────────────────────────────────────────────────

/**
 * Whether the signed-in actor may see the member list at all — mirrors the
 * Member entity's OR-gate (tickets.members.view OR tickets.members.invite):
 * invite authority implies member-list visibility, so a custom role granted
 * invite-without-view can't silently lose the member list. Gates both the
 * Team nav-rail item and the members-view route landing (admin-shell.view.ts).
 */
export const canViewMembers = computed(() => {
	const perms = activeActor.get()?.permissions ?? [];
	return (
		perms.includes("tickets.members.view") ||
		perms.includes("tickets.members.invite")
	);
});

/** Size of the member list — active rows only; a pending invite isn't a member yet. */
export const memberCount = computed(
	() => membersState.value.filter((m) => m.status === "active").length,
);

/** Count of unredeemed pending invites — the header's "· N pending" signal. */
export const pendingCount = computed(
	() => membersState.value.filter((m) => m.status === "pending").length,
);

/** Avatar initials from the display label, falling back to the email's first
 *  two characters when the label yields none. One definition for both the
 *  member-list rows (`grouped`) and the detail panel (`memberView`). */
function initialsOf(label: string, email: string): string {
	const parts = label.split(" ");
	return (
		((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() ||
		email.slice(0, 2).toUpperCase()
	);
}

// ── Role-grouped member list ──────────────────────────────────────────────────

/**
 * Role groups — active members AND pending invites bucketed by roleId,
 * ordered most-permissioned first so the list reads down the privilege
 * hierarchy. The count is `role.permissions.length` directly (no wildcard
 * grant, no isBuiltIn flag in this catalog — see role.entity.ts). Ties
 * break on name for a stable order across re-renders.
 */
export const grouped = computed(() => {
	const members = membersState.value;
	return rolesState.value
		.map((role) => {
			const activeMembers = members
				.filter(
					(m): m is Extract<Member.Member, { status: "active" }> =>
						m.status === "active" && m.roleId === role.id,
				)
				.map((m) => {
					const label = m.displayName ?? m.email;
					return { ...m, label, initials: initialsOf(label, m.email) };
				});
			const pendingInvites = members.filter(
				(m): m is Extract<Member.Member, { status: "pending" }> =>
					m.status === "pending" && m.roleId === role.id,
			);
			return {
				role,
				grantCount: role.permissions.length,
				members: activeMembers,
				invites: pendingInvites,
				total: activeMembers.length + pendingInvites.length,
			};
		})
		.filter((g) => g.total > 0)
		.toSorted(
			(a, b) =>
				b.grantCount - a.grantCount || a.role.name.localeCompare(b.role.name),
		);
});

/**
 * Member-list phase — "waiting for first snapshot" vs. "snapshot arrived and
 * is empty" vs. a live network error vs. a Firestore permission denial vs. a
 * populated list, so members renders exactly one of its five frames
 * with no derivation of its own. "denied" and "error" stay distinct so the
 * view never blames the connection for an access problem.
 * (Named `memberListState`, not `membersView`: `MembersView`/`members-view`
 * is the web route component and `memberView` the detail view-model beside it.)
 */
export const memberListState = computed<
	"loading" | "error" | "denied" | "empty" | "populated"
>(() => {
	if (membersStatus.value === "loading") return "loading";
	if (membersStatus.value === "error") return "error";
	if (membersStatus.value === "denied") return "denied";
	return grouped.get().length === 0 ? "empty" : "populated";
});

// ── Detail view-models (presentation derived here, never in views) ────────────

/**
 * Member-detail view-model — identity, current role, its grants, and the
 * guardrails governing this member's role-change and remove controls:
 *
 *   - self:          the actor viewing their own member-detail panel can't
 *                     change their own role or remove themselves.
 *   - sole owner:     the org's only holder of the built-in 'owner' role
 *                     (the sentinel docs/system-design/database/access.md's sole-owner
 *                     webhook pins) can't be demoted or removed by anyone,
 *                     so the org is never left without an Owner.
 *
 * `scope` rides along unchanged (member-detail.view.ts forwards it on the
 * assignGrant/removeGrant intent) so the write reaches every grant doc this
 * member holds — one at the org id for an all-events member, one per event
 * id for an event-scoped one.
 */
export const memberView = computed(() => {
	const detail = detailState.value;
	if (detail.kind !== "member") return null;
	// Active rows only — detailState's 'member' variant addresses a live
	// grant holder by uid; a pending invite has no uid and is addressed
	// through 'invite-detail' instead (see file header).
	const member = membersState.value.find(
		(m): m is Extract<Member.Member, { status: "active" }> =>
			m.status === "active" && m.uid === detail.uid,
	);
	if (!member) return null;
	const role = rolesState.value.find((r) => r.id === member.roleId);
	const label = member.displayName ?? member.email;
	const lang = uiLanguage();

	// Provenance line ("Granted by {name} on {date}") — member.entity.ts's
	// grantedBy/grantedAt fields. The provisioning sentinel
	// ("system:provision-member", written by provision-member.ts) has no
	// member row and reads as a human phrase, not the raw uid-shaped
	// string; grantedBy is nullish for a grant that predates the field, in
	// which case there is no provenance line at all.
	const grantor = member.grantedBy
		? membersState.value.find(
				(m): m is Extract<Member.Member, { status: "active" }> =>
					m.status === "active" && m.uid === member.grantedBy,
			)
		: undefined;
	const grantedByLabel = !member.grantedBy
		? null
		: member.grantedBy === "system:provision-member"
			? translate(lang, "admin.members.detail.initialProvisioning")
			: (grantor?.displayName ??
				grantor?.email ??
				translate(lang, "admin.members.detail.formerTeamMember"));
	const grantedAtLabel =
		formatDate(
			member.grantedAt.toDate().toISOString(),
			"en-US",
			INVITE_DATE_FORMAT,
		) ?? "—";

	const actor = activeActor.get();
	const canManageMembers =
		actor?.permissions.includes("tickets.members.invite") ?? false;
	const isSelf = member.uid === actor?.uid;
	const isSoleOwner =
		member.roleId === "owner" &&
		membersState.value.filter(
			(m) => m.status === "active" && m.roleId === "owner",
		).length === 1;
	const roleLockReason = isSelf
		? translate(lang, "admin.members.detail.roleLockSelf")
		: isSoleOwner
			? translate(lang, "admin.members.detail.soleOwnerLockReason")
			: null;
	const removeLockReason = isSelf
		? translate(lang, "admin.members.detail.removeLockSelf")
		: isSoleOwner
			? translate(lang, "admin.members.detail.soleOwnerLockReason")
			: null;

	return {
		uid: member.uid,
		displayName: label,
		email: member.email,
		roleId: member.roleId,
		scope: member.scope,
		grants: role?.permissions ?? [],
		initials: initialsOf(label, member.email),
		scopeLabel:
			member.scope.kind === "all-events"
				? translate(lang, "admin.members.detail.scopeAllEvents")
				: translate(
						lang,
						member.scope.eventIds.length === 1
							? "admin.members.detail.scopeEventsOne"
							: "admin.members.detail.scopeEventsOther",
						{ count: member.scope.eventIds.length },
					),
		canManageMembers,
		canChangeRole: canManageMembers && roleLockReason === null,
		canRemove: canManageMembers && removeLockReason === null,
		roleLockReason,
		removeLockReason,
		grantedByLabel,
		grantedAtLabel,
	};
});

/** Role-detail view-model — everything the role surface renders. Null when no role is open. */
export const roleView = computed(() => {
	const detail = detailState.value;
	if (detail.kind !== "role") return null;
	const {
		roleId: draftRoleId,
		name: draftName,
		perms,
		editing,
	} = roleDraft.value;
	const canonical = detail.id
		? rolesState.value.find((r) => r.id === detail.id)
		: undefined;
	// The draft governs this surface only while it drafts THIS role — the same
	// `undefined` on both sides for the create form. A draft belonging to
	// another role, or to a create form that was dismissed, reads as no draft
	// at all rather than rendering over the role actually open.
	const drafting = editing && draftRoleId === detail.id;
	// Display set: the draft while editing; otherwise the role's own granted set.
	const selected: Set<Permission> = drafting
		? perms
		: new Set(canonical?.permissions ?? []);
	const affected = detail.id
		? membersState.value.filter((m) => m.roleId === detail.id).length
		: 0;
	return {
		editing: drafting,
		name: drafting ? draftName : (canonical?.name ?? ""),
		selected,
		granted: selected.size,
		affected,
		canSave: drafting && draftName.trim().length > 0 && perms.size > 0,
		isBuiltIn: canonical?.isBuiltIn ?? false,
	};
});

/**
 * Pending-invite detail view-model — the live pending Member row addressed
 * by the discriminant's token (an existing member-list row or one just sent),
 * its role's grants, and expiry urgency. Null when not open, or when the
 * token no longer resolves — revoke deletes the row and resend replaces it
 * with a fresh token, so both close the panel rather than race the listener
 * for the replacement row (see invite-detail.view.ts).
 *
 * expiryUrgencyLabel/expiryTone bucket off hoursLeft (reused across both):
 * expired → error; <48h → warning ("Expires tomorrow" for the 24–48h band,
 * "Expires in N hours" under 24h); otherwise → default, "Expires in N days".
 * expiresLabel (the absolute date) stays available alongside it.
 */
export const inviteDetailView = computed(() => {
	const detail = detailState.value;
	if (detail.kind !== "invite-detail") return null;
	const invite = membersState.value.find(
		(m): m is Extract<Member.Member, { status: "pending" }> =>
			m.status === "pending" && m.token === detail.token,
	);
	if (!invite) return null;
	const role = rolesState.value.find((r) => r.id === invite.roleId);

	const hoursLeft =
		(invite.expiresAt.toDate().getTime() - Date.now()) / (60 * 60 * 1000);
	const isExpired = !Number.isFinite(hoursLeft) || hoursLeft <= 0;
	const roundedHours = Math.ceil(hoursLeft);
	const roundedDays = Math.ceil(hoursLeft / 24);
	const expiryTone: "error" | "warning" | "default" = isExpired
		? "error"
		: hoursLeft < 48
			? "warning"
			: "default";
	const lang = uiLanguage();
	const expiryUrgencyLabel = isExpired
		? translate(lang, "admin.members.inviteDetail.expired")
		: hoursLeft < 24
			? translate(
					lang,
					roundedHours === 1
						? "admin.members.inviteDetail.expiresInHoursOne"
						: "admin.members.inviteDetail.expiresInHoursOther",
					{ count: roundedHours },
				)
			: hoursLeft < 48
				? translate(lang, "admin.members.inviteDetail.expiresTomorrow")
				: translate(
						lang,
						roundedDays === 1
							? "admin.members.inviteDetail.expiresInDaysOne"
							: "admin.members.inviteDetail.expiresInDaysOther",
						{ count: roundedDays },
					);

	const scopeEventIds =
		invite.scope.kind === "events" ? invite.scope.eventIds : [];
	const options = eventOptions.value;

	return {
		invite,
		role,
		grants: role?.permissions ?? [],
		invitedLabel:
			formatDate(
				invite.createdAt.toDate().toISOString(),
				"en-US",
				INVITE_DATE_FORMAT,
			) ?? "—",
		expiresLabel:
			formatDate(
				invite.expiresAt.toDate().toISOString(),
				"en-US",
				INVITE_DATE_FORMAT,
			) ?? "—",
		expiryUrgencyLabel,
		expiryTone,
		isAllEvents: invite.scope.kind === "all-events",
		scopeEventNames: scopeEventIds.map(
			(id) => options.find((e) => e.id === id)?.name ?? id,
		),
	};
});

/** Invite-form view-model — draft, selected role, scope state. */
export const inviteView = computed(() => {
	const draft = inviteDraft.value;
	const selectedRole = rolesState.value.find((r) => r.id === draft.roleId);
	const isSpecific = draft.scope.kind === "events";
	const selectedEventIds =
		draft.scope.kind === "events" ? draft.scope.eventIds : [];
	return {
		draft,
		selectedRole,
		isSpecific,
		selectedEventIds,
		canSend:
			draft.email.trim().length > 0 &&
			draft.displayName.trim().length > 0 &&
			draft.roleId.length > 0 &&
			!(isSpecific && selectedEventIds.length === 0),
	};
});

// ── write-intent channel ────────────────────────────────────────────────────
// Every write a members view needs (assign/remove a grant, send/resend/
// revoke an invite, create/edit/delete a role) is zero-I/O intent composed
// here and pushed to membersAction$; members.sync.ts (web-side) is the sole
// subscriber, performing the Firestore writes and the createInvite callable
// call, and reconciling ok/error back via membersResult$, keyed by
// requestId so a view only reacts to the result of the request it made.

export type MembersAction =
	| {
			kind: "assignGrant";
			requestId: string;
			targetUid: string;
			roleId: string;
			/** The member's own scope — resolved into the actual grant-doc id(s) at the web seam. */
			scope: Member.MemberScope;
	  }
	| {
			kind: "removeGrant";
			requestId: string;
			targetUid: string;
			/** The member's own scope — resolved into the actual grant-doc id(s) at the web seam. */
			scope: Member.MemberScope;
	  }
	| { kind: "invite"; requestId: string; draft: InviteDraft }
	| {
			kind: "resendInvite";
			requestId: string;
			token: string;
			draft: InviteDraft;
	  }
	| { kind: "revokeInvite"; requestId: string; token: string }
	| {
			kind: "editInvite";
			requestId: string;
			token: string;
			patch: { roleId?: string; displayName?: string };
	  }
	| {
			kind: "createRole";
			requestId: string;
			name: string;
			permissions: readonly Permission[];
	  }
	| {
			kind: "editRole";
			requestId: string;
			roleId: string;
			patch: { name?: string; permissions?: readonly Permission[] };
	  }
	| { kind: "deleteRole"; requestId: string; roleId: string };

export const membersAction$ = new Subject<MembersAction>();

export interface MembersResult {
	readonly requestId: string;
	readonly kind: MembersAction["kind"];
	readonly ok: boolean;
	/** The generated role id — carried on a successful 'createRole' result. */
	readonly roleId?: string;
	/** The minted invite — carried on a successful 'invite'/'resendInvite' result. */
	readonly invite?: {
		readonly token: string;
		readonly createdAt: string;
		readonly expiresAt: string;
	};
	readonly error?: string;
}

export const membersResult$ = new Subject<MembersResult>();
